// DESIGN-SPEC §5.14 Sheet 底部浮层
"use client";

import type { ReactNode } from "react";
import { useEffect, useId, useState } from "react";
import { X } from "lucide-react";
import { cn } from "@/lib/cn";
import { Button } from "./button";

export function Sheet({
  open,
  onOpenChange,
  title,
  children,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  children: ReactNode;
}) {
  const titleId = useId();

  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") onOpenChange(false);
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [onOpenChange, open]);

  return (
    <div
      aria-hidden={!open}
      className={cn(
        "fixed inset-0 z-[80] motion-safe:transition-[visibility] motion-safe:duration-[240ms]",
        open ? "pointer-events-auto visible" : "pointer-events-none invisible",
      )}
    >
      <button
        type="button"
        tabIndex={-1}
        aria-label="关闭浮层"
        onClick={() => onOpenChange(false)}
        className={cn(
          "absolute inset-0 bg-forest-900/50 motion-safe:transition-opacity motion-safe:duration-[240ms] motion-safe:ease-out",
          open ? "opacity-100" : "opacity-0",
        )}
      />
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={cn(
          "responsive-sheet absolute right-0 bottom-0 left-0 mx-auto max-h-[88dvh] max-w-[560px] overflow-y-auto rounded-t-[24px] border-t border-line bg-paper px-5 pb-[calc(24px+env(safe-area-inset-bottom))] shadow-[0_-28px_80px_rgba(16,40,27,0.22)] motion-safe:transition-transform motion-safe:duration-[240ms] motion-safe:ease-out",
          open ? "translate-y-0" : "translate-y-full",
        )}
      >
        <div aria-hidden="true" className="mx-auto h-9 w-10 pt-3">
          <span className="block h-1 w-10 rounded-full bg-line-strong" />
        </div>
        <div className="flex items-center justify-between gap-4 border-b border-line pb-4">
          <div>
            <p className="font-mono text-[9px] tracking-[0.16em] text-ink-3">DETAIL / ACTION</p>
            <h2 id={titleId} className="mt-1 font-display text-xl leading-7 font-semibold text-ink">
              {title}
            </h2>
          </div>
          <button
            type="button"
            aria-label="关闭"
            onClick={() => onOpenChange(false)}
            className="flex size-11 shrink-0 items-center justify-center rounded-full border border-line bg-card text-ink-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-forest-500"
          >
            <X className="size-4" aria-hidden="true" />
          </button>
        </div>
        <div className="mt-4">{children}</div>
      </section>
    </div>
  );
}

export function SheetDemo({
  triggerLabel = "打开底部浮层",
  children,
}: {
  triggerLabel?: string;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)}>
        {triggerLabel}
      </Button>
      <Sheet open={open} onOpenChange={setOpen} title="咨询客服">
        {children}
        <Button
          variant="brand"
          className="mt-5 w-full"
          onClick={() => setOpen(false)}
        >
          知道了
        </Button>
      </Sheet>
    </>
  );
}
